import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Box, Button, Chip, CircularProgress, Collapse, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BaseModalComponent } from "src/components/common/BaseModalComponent";
import { InvoiceCreateAdapter } from "src/components/invoice/create/InvoiceCreateView";
import { useModals } from "src/context/ModalContext";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import type { UsagePeriodSummaryItem } from "../actions/get-period-summaries.action";
import { useInvoiceByExternalUidQuery } from "../hooks/useInvoiceByExternalUidQuery";
import { useInfinitePeriodSummariesQuery } from "../hooks/usePeriodSummariesQuery";
import { parsePhoneBillingToGenericInvoice } from "@components/invoice/create/utils/parsePhoneBilling";

const ORANGE = "#FB8E00";
const PAGE_SIZE = 3;
const GRACE_PERIOD_DAYS = 5;

function formatBillingPeriodLabel(period: string): string {
  const [year, month] = period.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

interface PlanMonthlyInvoicesListProps {
  phoneNumber: string;
  planSubscriptionId: string;
}

export function PlanMonthlyInvoicesList({ phoneNumber, planSubscriptionId }: PlanMonthlyInvoicesListProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfinitePeriodSummariesQuery(
    phoneNumber,
    isOpen,
    "voice,sms",
    PAGE_SIZE,
  );

  const summaries = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages]);

  const total = data?.pages[0]?.total ?? summaries.length;
  const visibleSummaries = summaries.slice(0, visibleCount);
  const canShowMore = visibleCount < summaries.length || hasNextPage;

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleShowMore = async () => {
    const nextVisible = visibleCount + PAGE_SIZE;

    if (nextVisible > summaries.length && hasNextPage) {
      await fetchNextPage();
    }

    setVisibleCount(nextVisible);
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1.25,
          border: "none",
          bgcolor: isOpen ? "rgba(251, 142, 0, 0.04)" : "transparent",
          cursor: "pointer",
          textAlign: "left",
          transition: "background-color 0.15s ease",
          "&:hover": {
            bgcolor: "rgba(251, 142, 0, 0.06)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: ORANGE }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "var(--fx-surface-textsecondary, #64748B)",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            Monthly invoices
          </Typography>
          {isOpen && !isLoading && total > 0 ? <Typography sx={{ fontSize: 12, fontWeight: 700, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>({total})</Typography> : null}
        </Box>

        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 22,
            color: "var(--fx-surface-textsecondary, #64748B)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      <Collapse in={isOpen} timeout={200} unmountOnExit>
        <Box sx={{ borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2.5 }}>
              <CircularProgress size={22} sx={{ color: ORANGE }} />
            </Box>
          ) : isError ? (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#b91c1c", px: 1.5, py: 2 }}>Could not load monthly invoices.</Typography>
          ) : summaries.length === 0 ? (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", px: 1.5, py: 2 }}>No monthly invoices yet for this number.</Typography>
          ) : (
            <>
              {visibleSummaries.map((summary, index) => (
                <MonthlyInvoiceListItem
                  key={summary.uid}
                  summary={summary}
                  phoneNumber={phoneNumber}
                  planSubscriptionId={planSubscriptionId}
                  showDivider={index < visibleSummaries.length - 1 || canShowMore}
                />
              ))}

              {canShowMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <Button
                    size="small"
                    onClick={handleShowMore}
                    disabled={isFetchingNextPage}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: ORANGE,
                      fontSize: 12,
                    }}
                  >
                    {isFetchingNextPage ? "Loading..." : "Show 3 more"}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function MonthlyInvoiceListItem({
  summary,
  phoneNumber,
  planSubscriptionId,
  showDivider,
}: {
  summary: UsagePeriodSummaryItem;
  phoneNumber: string;
  planSubscriptionId: string;
  showDivider: boolean;
}) {
  const company = useSelector((state: any) => state.userState.company);
  const { openModal } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const [invoiceData, setInvoiceData] = useState<any>(null);

  const modalKey = `Dashboard-GenericInvoiceCreate-${planSubscriptionId}-${summary.uid}`;

  const { data: invoiceResponse, isFetching: isFetchingInvoice } = useInvoiceByExternalUidQuery(summary.uid, true);

  const existingInvoice = useMemo(() => {
    const resData = invoiceResponse;
    const invoices = resData && typeof resData === "object" && "data" in resData ? resData.data : resData;
    return Array.isArray(invoices) ? invoices[0] : invoices;
  }, [invoiceResponse]);

  const limitDateFormatted = useMemo(() => {
    const baseDateStr = existingInvoice?.createdAt || summary.closedAt;
    if (!baseDateStr) return "";
    const limitDate = new Date(baseDateStr);
    limitDate.setDate(limitDate.getDate() + GRACE_PERIOD_DAYS);
    return limitDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [existingInvoice?.createdAt, summary.closedAt]);

  const hasInvoice = !!existingInvoice;
  const billableServices = (summary.services || []).filter((service) => Number(service.units) > 0 && Number(service.totalAmount) > 0);
  const canGenerate = billableServices.length > 0 && summary.grandTotal > 0;

  const buildInvoiceData = () => {
    const genericInput = parsePhoneBillingToGenericInvoice(summary);

    genericInput.client = {
      name: company?.businessName || company?.name || "Company Customer",
      alias: summary.phoneNumber,
      email: company?.email || "",
      phone: company?.phone || summary.phoneNumber,
      avatar: company?.logo || company?.avatar || "",
    };

    return genericInput;
  };

  const handleGenerate = () => {
    if (!canGenerate) {
      showSnackbar("No billable usage for this period.", "warning");
      return;
    }

    const data = buildInvoiceData();

    if (!data.lineItems?.length) {
      showSnackbar("No billable line items mapped for this period.", "warning");
      return;
    }

    setInvoiceData(data);
    openModal(modalKey);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        px: 1.5,
        py: 1.25,
        borderBottom: showDivider ? "1px solid rgba(0,0,0,0.06)" : "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>
            {formatBillingPeriodLabel(summary.billingPeriod)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", mt: 0.35 }}>
            {billableServices.length} service{billableServices.length === 1 ? "" : "s"} · Closed{" "}
            {new Date(summary.closedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 14, fontWeight: 900, color: ORANGE, flexShrink: 0 }}>{formatUsd(summary.grandTotal)}</Typography>

        {isFetchingInvoice && !existingInvoice ? (
          <CircularProgress size={16} sx={{ color: ORANGE }} />
        ) : hasInvoice && existingInvoice ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
            {existingInvoice.paidAt ? (
              <Chip
                label="PAID"
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: "rgba(76, 175, 80, 0.08)",
                  color: "#2e7d32",
                  border: "1px solid rgba(76, 175, 80, 0.16)",
                }}
              />
            ) : (
              <Chip
                label="UNPAID"
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: "rgba(244, 67, 54, 0.08)",
                  color: "#d32f2f",
                  border: "1px solid rgba(244, 67, 54, 0.16)",
                }}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const invoiceUrl = existingInvoice.invoiceUrl || `/invoices/${existingInvoice.uid}`;
                window.open(invoiceUrl, "_blank");
              }}
              sx={{
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
                borderRadius: "8px",
                borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.12)",
                color: "var(--fx-surface-textsecondary, #64748B)",
                "&:hover": {
                  borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.24)",
                },
              }}
            >
              View Invoice
            </Button>
          </Box>
        ) : (
          <BaseModalComponent
            modalKey={modalKey}
            modalLabel={`Invoice · ${formatBillingPeriodLabel(summary.billingPeriod)}`}
            defaultSizes={{ width: { xs: "95%", md: "85%", lg: "1200px" }, height: { xs: "95%", md: "85%" } }}
            elementTrigger={
              <Button
                size="small"
                variant="outlined"
                disabled={!canGenerate}
                onClick={handleGenerate}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: "8px",
                  flexShrink: 0,
                  borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.12)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                }}
              >
                Generate Invoice
              </Button>
            }
          >
            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              {invoiceData ? (
                <InvoiceCreateAdapter initialData={invoiceData} invoiceModal={modalKey} phoneNumber={phoneNumber} />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, p: 5 }}>
                  <CircularProgress size={40} />
                </Box>
              )}
            </Box>
          </BaseModalComponent>
        )}
      </Box>

      {/* Warning message below */}
      {hasInvoice && existingInvoice && !existingInvoice.paidAt && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: "var(--fx-surface-textsecondary, #64748B)",
            textAlign: "right",
            mt: 0.25,
            mr: 1,
          }}
        >
          Payment due by <strong style={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700 }}>{limitDateFormatted}</strong> to avoid service interruption.
        </Typography>
      )}
    </Box>
  );
}
